import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/types/enums";

const ADMIN_ROLES: string[] = [UserRole.SUPERADMIN, UserRole.DIRECTOR_TECH];

export default function HamburgerMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);

  const isAdmin = user && ADMIN_ROLES.includes(user.role);

  const close = useCallback(() => setIsOpen(false), []);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") close();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, close]);

  return (
    <div ref={menuRef} className="sm:hidden">
      {/* Toggle button */}
      <button
        type="button"
        aria-label="Menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className="sm:hidden inline-flex items-center justify-center p-2 min-h-[44px] min-w-[44px] rounded text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors"
      >
        {/* Hamburger icon */}
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          {isOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Dropdown overlay */}
      {isOpen && (
        <div className="absolute right-0 top-16 z-50 w-64 rounded-lg border border-gray-200 bg-white py-2 shadow-lg transition-all">
          <nav className="flex flex-col">
            <Link
              to="/dashboard"
              onClick={close}
              className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
            >
              Dashboard
            </Link>
            {user && user.role !== UserRole.CLIENT && (
              <Link
                to="/drills/new"
                onClick={close}
                className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
              >
                Nuevo Simulacro
              </Link>
            )}
            <Link
              to="/archive"
              onClick={close}
              className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
            >
              Archivo
            </Link>
            {isAdmin && (
              <>
                <div className="my-1 border-t border-gray-100" />
                <Link
                  to="/admin/companies"
                  onClick={close}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  Empresas
                </Link>
                <Link
                  to="/admin/sites"
                  onClick={close}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  Centros
                </Link>
                <Link
                  to="/admin/users"
                  onClick={close}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  Usuarios
                </Link>
                <Link
                  to="/legacy-documents"
                  onClick={close}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  Documentos
                </Link>
                <Link
                  to="/admin/review-templates"
                  onClick={close}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  Plantillas de Revisión
                </Link>
                <Link
                  to="/admin/drill-types"
                  onClick={close}
                  className="px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                >
                  Tipos de Simulacro
                </Link>
              </>
            )}
            {user && (
              <>
                <div className="my-1 border-t border-gray-100" />
                <div className="px-4 py-2 text-xs text-gray-500">
                  {user.first_name} {user.last_name} — {user.role}
                </div>
              </>
            )}
          </nav>
        </div>
      )}
    </div>
  );
}
